import { addendaEntries, isAddendaTable } from './addenda';
import {
  enforcementEntries,
  isEnforcementTable,
  relatedEnforcementSources,
  type EnforcementEntry,
} from './enforcement';
import { propertyFieldLabel, propertyRecordKindLabel } from './field-labels';
import { sf311CaseSourceUrl } from './presentation';
import { dedupeFireComplianceStatements } from '../skills/sf-fire-inspections/scripts/safety-compliance';
import type {
  AssessmentRoll,
  AssessorRecord,
  FireComplianceStatement,
  NormalizedPage,
  ParcelRecord,
  ParcelSnapshot,
  PublicWorksSnapshot,
  RecorderDocument,
  RecorderSnapshot,
  RecordChange,
  Sf311Case,
  Sf311Snapshot,
  SafetyComplianceSnapshot,
  SubdivisionProject,
  SubdivisionSnapshot,
} from './types';

function deadlineNote(value: string) {
  if (/deadline|due|reinspection|hearing|order of abatement|hold|respond by|appeal/i.test(value)) {
    return 'This update may contain a deadline or required action. Review the official record promptly.';
  }
  if (/issued|filed|open|violation|correction/i.test(value)) {
    return 'Review the official record to confirm whether an inspection, correction, or response is required.';
  }
  return 'No explicit deadline was detected in this change; confirm details in the official record.';
}

type SafetyRecord = {
  id: string;
  sourceUrl: string;
  fields: Record<string, string>;
};

function safetyAction(kind: RecordChange['kind'], value: string) {
  if (kind === 'section604') {
    return 'Review the official Section 604 affidavit record. This dataset is published yearly, so confirm recent filings directly with DBI when timing matters.';
  }
  if (kind === 'fire-violation') {
    return 'Review the SFFD violation and corrective action promptly; an open violation may require correction or reinspection.';
  }
  if (kind === 'fire-inspection') {
    return deadlineNote(value);
  }
  return 'Review the official Statement of Compliance document and confirm the property’s applicable biennial filing cycle with SFFD.';
}

function safetySummary(record: SafetyRecord, kind: RecordChange['kind']) {
  return (
    Object.entries(record.fields)
      .filter(([, value]) => value)
      .slice(0, 4)
      .map(([field, value]) => `${propertyFieldLabel(field, kind)}: ${value}`)
      .join(' · ') || record.id
  );
}

function diffSafetyRecords(
  kind: Extract<
    RecordChange['kind'],
    'section604' | 'fire-inspection' | 'fire-violation' | 'fire-compliance'
  >,
  label: string,
  previous: SafetyRecord[],
  current: SafetyRecord[],
) {
  const changes: RecordChange[] = [];
  const oldRecords = new Map(previous.map((record) => [record.id, record]));
  const newRecords = new Map(current.map((record) => [record.id, record]));
  for (const [id, record] of newRecords) {
    const oldRecord = oldRecords.get(id);
    if (!oldRecord) {
      changes.push({
        recordId: id,
        kind,
        field: label,
        oldValue: 'Not present',
        newValue: safetySummary(record, kind),
        sourceUrl: record.sourceUrl,
        action: safetyAction(kind, safetySummary(record, kind)),
      });
      continue;
    }
    for (const field of new Set([
      ...Object.keys(oldRecord.fields),
      ...Object.keys(record.fields),
    ])) {
      // Dataset refresh clocks do not describe a property change.
      if (
        /^(data_as_of|data_loaded_at|last_updated|last_update|updated_at|data_updated)$/i.test(
          field,
        )
      )
        continue;
      const before = oldRecord.fields[field] ?? 'Not present';
      const after = record.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: id,
        kind,
        field: `${label} · ${field}`,
        oldValue: before,
        newValue: after,
        sourceUrl: record.sourceUrl,
        action: safetyAction(kind, `${field} ${after}`),
      });
    }
  }
  for (const [id, record] of oldRecords) {
    if (newRecords.has(id)) continue;
    changes.push({
      recordId: id,
      kind,
      field: label,
      oldValue: safetySummary(record, kind),
      newValue: 'Removed from the published source',
      sourceUrl: record.sourceUrl,
      action: `Confirm the removal in the official ${label} source before relying on it.`,
    });
  }
  return changes;
}

function diffSafetyCompliance(
  previous: SafetyComplianceSnapshot,
  current: SafetyComplianceSnapshot,
) {
  return [
    ...diffSafetyRecords(
      'section604',
      'Section 604 affidavit',
      previous.section604.affidavits,
      current.section604.affidavits,
    ),
    ...diffSafetyRecords(
      'fire-inspection',
      'SFFD fire inspection',
      previous.fireInspections.inspections,
      current.fireInspections.inspections,
    ),
    ...diffSafetyRecords(
      'fire-violation',
      'SFFD fire violation',
      previous.fireViolations.violations,
      current.fireViolations.violations,
    ),
    ...diffSafetyRecords(
      'fire-compliance',
      'SFFD Statement of Compliance',
      previous.statements.statements,
      current.statements.statements,
    ),
  ];
}

function complianceDedupeIdentity(statement: FireComplianceStatement) {
  const occupant = statement.fields?.occupant_number;
  if (
    typeof statement.address !== 'string' ||
    typeof statement.date !== 'string' ||
    typeof occupant !== 'string' ||
    !/^\d+$/.test(occupant) ||
    !/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(statement.date)
  )
    return null;

  const day = statement.date.slice(0, 10);
  const timestamp = Date.parse(day);
  if (
    !Number.isFinite(timestamp) ||
    new Date(timestamp).toISOString().slice(0, 10) !== day ||
    !Number.isFinite(Date.parse(statement.date))
  )
    return null;

  // Match the building normalization used by dedupeFireComplianceStatements.
  // A migration exemption also requires a recognizable building address.
  const address = statement.address
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
    .replace(/,.*$/, '')
    .replace(/\s+#.*$/, '')
    .replace(/\s+(?:UNIT|APT)\s+\S+$/, '')
    .replace(/\bAV\b/g, 'AVE')
    .replace(/[^A-Z0-9 -]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!/^\d+[A-Z]?(?:-\d+[A-Z]?)?\s+[A-Z0-9]/.test(address)) return null;
  return JSON.stringify([address, statement.date, occupant]);
}

function removeProvenComplianceDuplicates(
  previous: FireComplianceStatement[],
  current: FireComplianceStatement[],
) {
  // The upgrade can discard an imported copy only when the prior canonical
  // winner survives with its identity unchanged. An older copy is not enough.
  const previousIds = new Set(previous.map((statement) => statement.id));
  const currentById = new Map(current.map((statement) => [statement.id, statement]));
  if (
    previousIds.size !== previous.length ||
    currentById.size !== current.length ||
    [...previous, ...current].some(
      (statement) => typeof statement.id !== 'string' || !statement.id.trim(),
    )
  )
    return previous;

  const retainedIdentities = new Set<string>();
  const previousWinners = dedupeFireComplianceStatements(
    previous.filter((statement) => complianceDedupeIdentity(statement) !== null),
  );
  for (const old of previousWinners) {
    const statement = currentById.get(old.id);
    const identity = complianceDedupeIdentity(old);
    if (statement && identity && complianceDedupeIdentity(statement) === identity)
      retainedIdentities.add(identity);
  }
  return previous.filter((statement) => {
    if (currentById.has(statement.id)) return true;
    const identity = complianceDedupeIdentity(statement);
    return !identity || !retainedIdentities.has(identity);
  });
}

function newSafetySourceChanges(current: SafetyComplianceSnapshot) {
  const sources: Array<{
    kind: Extract<
      RecordChange['kind'],
      'section604' | 'fire-inspection' | 'fire-violation' | 'fire-compliance'
    >;
    label: string;
    records: SafetyRecord[];
    sourceUrl: string;
  }> = [
    {
      kind: 'section604',
      label: 'Section 604 affidavits',
      records: current.section604.affidavits,
      sourceUrl: current.section604.sourceUrl,
    },
    {
      kind: 'fire-inspection',
      label: 'SFFD fire inspections',
      records: current.fireInspections.inspections,
      sourceUrl: current.fireInspections.sourceUrl,
    },
    {
      kind: 'fire-violation',
      label: 'SFFD fire violations',
      records: current.fireViolations.violations,
      sourceUrl: current.fireViolations.sourceUrl,
    },
    {
      kind: 'fire-compliance',
      label: 'SFFD Statements of Compliance',
      records: current.statements.statements,
      sourceUrl: current.statements.sourceUrl,
    },
  ];
  return sources.flatMap(({ kind, label, records, sourceUrl }) => {
    if (!records.length) return [];
    const latest = records[0];
    return [
      {
        recordId: `${records.length} record${records.length === 1 ? '' : 's'}`,
        kind,
        field: `New monitoring source · ${label}`,
        oldValue: 'This source was not included in the prior baseline',
        newValue: `${records.length} existing record${records.length === 1 ? '' : 's'} found${latest ? ` · Latest: ${safetySummary(latest, kind)}` : ''}`,
        sourceUrl: latest?.sourceUrl || sourceUrl,
        action:
          'Review the existing records from this newly added source. They may predate Parcel Watch monitoring and are not necessarily newly filed city records.',
      } satisfies RecordChange,
    ];
  });
}

function expandedCondoSafetyChanges(
  previous: SafetyComplianceSnapshot,
  current: SafetyComplianceSnapshot,
) {
  const sources: Array<{
    kind: Extract<
      RecordChange['kind'],
      'section604' | 'fire-inspection' | 'fire-violation' | 'fire-compliance'
    >;
    label: string;
    previousRecords: SafetyRecord[];
    currentRecords: SafetyRecord[];
    sourceUrl: string;
  }> = [
    {
      kind: 'section604',
      label: 'Section 604 affidavits',
      previousRecords: previous.section604.affidavits,
      currentRecords: current.section604.affidavits,
      sourceUrl: current.section604.sourceUrl,
    },
    {
      kind: 'fire-inspection',
      label: 'SFFD fire inspections',
      previousRecords: previous.fireInspections.inspections,
      currentRecords: current.fireInspections.inspections,
      sourceUrl: current.fireInspections.sourceUrl,
    },
    {
      kind: 'fire-violation',
      label: 'SFFD fire violations',
      previousRecords: previous.fireViolations.violations,
      currentRecords: current.fireViolations.violations,
      sourceUrl: current.fireViolations.sourceUrl,
    },
    {
      kind: 'fire-compliance',
      label: 'SFFD Statements of Compliance',
      previousRecords: previous.statements.statements,
      currentRecords: current.statements.statements,
      sourceUrl: current.statements.sourceUrl,
    },
  ];
  return sources.flatMap(({ kind, label, previousRecords, currentRecords, sourceUrl }) => {
    const previousIds = new Set(previousRecords.map((record) => record.id));
    const additions = currentRecords.filter((record) => !previousIds.has(record.id));
    if (!additions.length) return [];
    const latest = additions[0];
    if (!latest) return [];
    return [
      {
        recordId: `${additions.length} record${additions.length === 1 ? '' : 's'}`,
        kind,
        field: `Expanded monitoring scope · ${label}`,
        oldValue: 'Not present in the prior parcel-only baseline',
        newValue: `${additions.length} existing building-level record${additions.length === 1 ? '' : 's'} found · Latest: ${safetySummary(latest, kind)}`,
        sourceUrl: latest?.sourceUrl || sourceUrl,
        action:
          'Review these existing records from the shared condo building. They may predate Parcel Watch monitoring and are not necessarily newly filed city records.',
      } satisfies RecordChange,
    ];
  });
}

function scalarFields(record: ParcelRecord) {
  const result: Record<string, string> = {
    Status: record.status,
    Date: record.date,
    Address: record.address,
    Description: record.description,
  };
  record.pages.forEach((page, pageIndex) => {
    Object.entries(page.fields).forEach(([key, value]) => {
      // DBI's print timestamp changes on retrieval, including in older baselines.
      if (/^report\s*date\s*:?$/i.test(key)) return;
      result[`Page ${pageIndex + 1} · ${key}`] = value;
    });
  });
  return result;
}

function tableRows(record: ParcelRecord) {
  const result = new Map<string, { page: NormalizedPage; rows: Map<string, number> }>();
  record.pages.forEach((page, pageIndex) => {
    page.tables.forEach((table, tableIndex) => {
      if (
        isAddendaTable(table) ||
        ((record.kind === 'complaint' || record.kind === 'nov') && isEnforcementTable(table))
      )
        return;
      const key = `Page ${pageIndex + 1} · ${table.title || `Table ${tableIndex + 1}`}`;
      const rows = new Map<string, number>();
      for (const row of table.rows) {
        const value = row.join(' · ');
        rows.set(value, (rows.get(value) ?? 0) + 1);
      }
      result.set(key, {
        page,
        rows,
      });
    });
  });
  return result;
}

function diffAddenda(oldRecord: ParcelRecord, newRecord: ParcelRecord) {
  const changes: RecordChange[] = [];
  const oldEntries = new Map(addendaEntries(oldRecord).map((entry) => [entry.key, entry]));
  const newEntries = new Map(addendaEntries(newRecord).map((entry) => [entry.key, entry]));

  for (const [key, entry] of newEntries) {
    const previous = oldEntries.get(key);
    if (!previous) {
      changes.push({
        recordId: newRecord.id,
        kind: newRecord.kind,
        field: 'Addenda Details · entry added',
        oldValue: 'Not present',
        newValue: entry.summary,
        sourceUrl: newRecord.sourceUrl,
        action: deadlineNote(entry.summary),
      });
      continue;
    }

    for (const field of new Set([...Object.keys(previous.fields), ...Object.keys(entry.fields)])) {
      const before = previous.fields[field] ?? 'Not present';
      const after = entry.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: newRecord.id,
        kind: newRecord.kind,
        field: `Addenda Details · ${field}`,
        oldValue: before,
        newValue: after,
        sourceUrl: newRecord.sourceUrl,
        action: deadlineNote(after),
      });
    }
  }

  for (const [key, entry] of oldEntries) {
    if (newEntries.has(key)) continue;
    changes.push({
      recordId: newRecord.id,
      kind: newRecord.kind,
      field: 'Addenda Details · entry removed',
      oldValue: entry.summary,
      newValue: 'Removed',
      sourceUrl: newRecord.sourceUrl,
      action: 'Confirm the removal in the official DBI record before relying on it.',
    });
  }

  return changes;
}

function diffEnforcement(oldRecord: ParcelRecord, newRecord: ParcelRecord) {
  const changes: RecordChange[] = [];
  const previousEntries = enforcementEntries(oldRecord);
  const currentEntries = enforcementEntries(newRecord);
  if (oldRecord.kind === 'complaint' && previousEntries.length > 0 && currentEntries.length === 0) {
    throw new Error(
      `DBI returned no case history for complaint ${newRecord.id}; baseline was preserved.`,
    );
  }
  // City history rows have no IDs. Match unchanged rows first within each
  // date/type/division group so inserts and sorting don't rewrite older events.
  // Arrays preserve repeated identical rows, unlike a Set of row contents.
  const oldEntries = new Map<string, EnforcementEntry[]>();
  for (const entry of previousEntries) {
    const group = oldEntries.get(entry.identity) ?? [];
    group.push(entry);
    oldEntries.set(entry.identity, group);
  }
  const newEntries = currentEntries.filter((entry) => {
    const group = oldEntries.get(entry.identity);
    const index =
      group?.findIndex((previous) => {
        const fields = new Set([...Object.keys(previous.fields), ...Object.keys(entry.fields)]);
        return [...fields].every((field) => previous.fields[field] === entry.fields[field]);
      }) ?? -1;
    if (index < 0) return true;
    group!.splice(index, 1);
    return false;
  });

  for (const entry of newEntries) {
    const previous = oldEntries.get(entry.identity)?.shift();
    if (!previous) {
      changes.push({
        recordId: newRecord.id,
        kind: newRecord.kind,
        field: `${entry.label} · entry added`,
        oldValue: 'Not present',
        newValue: entry.summary,
        sourceUrl: newRecord.sourceUrl,
        action: deadlineNote(entry.summary),
      });
      continue;
    }

    for (const field of new Set([...Object.keys(previous.fields), ...Object.keys(entry.fields)])) {
      const before = previous.fields[field] ?? 'Not present';
      const after = entry.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: newRecord.id,
        kind: newRecord.kind,
        field: `${entry.label} · ${field}`,
        oldValue: before,
        newValue: after,
        sourceUrl: newRecord.sourceUrl,
        action: deadlineNote(after),
      });
    }
  }

  for (const entry of [...oldEntries.values()].flat()) {
    changes.push({
      recordId: newRecord.id,
      kind: newRecord.kind,
      field: `${entry.label} · entry removed`,
      oldValue: entry.summary,
      newValue: 'Removed',
      sourceUrl: newRecord.sourceUrl,
      action: 'Confirm the removal in the official DBI record before relying on it.',
    });
  }

  return changes;
}

function recordLinks(record: ParcelRecord) {
  return new Set(
    record.pages.flatMap((page) => page.links.map((link) => `${link.label} · ${link.url}`)),
  );
}

function assessorAction(field: string) {
  if (/assessed|sale price/i.test(field)) {
    return 'This change may affect the parcel’s assessed value or property taxes. Review the official Assessor report and any applicable appeal deadline promptly.';
  }
  if (/sale date/i.test(field)) {
    return 'Confirm the transaction information in the official Assessor report and review any resulting assessment notices.';
  }
  return 'Review the updated property characteristic in the official Assessor report.';
}

function rollSummary(roll: AssessmentRoll) {
  const land = roll.fields['Assessed land value'];
  const improvements = roll.fields['Assessed improvement value'];
  return `Closed roll ${roll.year}${land ? ` · land ${land}` : ''}${improvements ? ` · improvements ${improvements}` : ''}`;
}

function diffAssessmentHistory(
  previous: AssessmentRoll[] | undefined,
  current: AssessmentRoll[] | undefined,
  parcelNumber: string,
) {
  if (previous === undefined || current === undefined) return [];
  const changes: RecordChange[] = [];
  const oldRolls = new Map(previous.map((roll) => [roll.year, roll]));
  const newRolls = new Map(current.map((roll) => [roll.year, roll]));

  for (const [year, roll] of newRolls) {
    const oldRoll = oldRolls.get(year);
    if (!oldRoll) {
      changes.push({
        recordId: `${parcelNumber} · ${year}`,
        kind: 'assessor',
        field: 'Assessment roll',
        oldValue: 'Not present',
        newValue: rollSummary(roll),
        sourceUrl: roll.sourceUrl,
        action:
          'A new closed assessment roll is available. Review its values and any applicable assessment appeal deadline.',
      });
      continue;
    }

    for (const field of new Set([...Object.keys(oldRoll.fields), ...Object.keys(roll.fields)])) {
      const before = oldRoll.fields[field] ?? 'Not present';
      const after = roll.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: `${parcelNumber} · ${year}`,
        kind: 'assessor',
        field: `Assessment ${year} · ${field}`,
        oldValue: before,
        newValue: after,
        sourceUrl: roll.sourceUrl,
        action: assessorAction(field),
      });
    }
  }

  for (const [year, roll] of oldRolls) {
    if (newRolls.has(year)) continue;
    changes.push({
      recordId: `${parcelNumber} · ${year}`,
      kind: 'assessor',
      field: 'Assessment roll',
      oldValue: rollSummary(roll),
      newValue: 'Removed from the published assessment history',
      sourceUrl: roll.sourceUrl,
      action:
        'Confirm the removal or correction with the official Assessor record before relying on it.',
    });
  }
  return changes;
}

function diffAssessor(
  previous: AssessorRecord | null,
  current: AssessorRecord | null,
  block: string,
  lot: string,
  includeHistory: boolean,
) {
  const recordId = `${block}/${lot}`;
  if (!previous && !current) return [];
  if (!previous && current) {
    if (!includeHistory && !Object.values(current.fields).some(Boolean)) return [];
    return [
      {
        recordId,
        kind: 'assessor' as const,
        field: 'Assessor record',
        oldValue: 'Not present',
        newValue: 'Assessor record added',
        sourceUrl: current.sourceUrl,
        action: 'Review the newly available official Assessor report.',
      },
    ];
  }
  if (previous && !current) {
    return [
      {
        recordId,
        kind: 'assessor' as const,
        field: 'Assessor record',
        oldValue: 'Assessor record present',
        newValue: 'Removed from the current Assessor report',
        sourceUrl: previous.sourceUrl,
        action:
          'Confirm whether the parcel was retired, renumbered, or otherwise changed by the Assessor-Recorder.',
      },
    ];
  }

  const changes: RecordChange[] = [];
  const oldFields = previous?.fields ?? {};
  const newFields = current?.fields ?? {};
  for (const field of new Set([...Object.keys(oldFields), ...Object.keys(newFields)])) {
    const before = oldFields[field] ?? 'Not present';
    const after = newFields[field] ?? 'Removed';
    if (before === after) continue;
    changes.push({
      recordId,
      kind: 'assessor',
      field,
      oldValue: before,
      newValue: after,
      sourceUrl: current?.sourceUrl ?? previous?.sourceUrl ?? '',
      action: assessorAction(field),
    });
  }
  if (includeHistory && previous && current) {
    changes.push(...diffAssessmentHistory(previous.history, current.history, current.parcelNumber));
  }
  return changes;
}

function diffRecord(
  oldRecord: ParcelRecord,
  newRecord: ParcelRecord,
  suppressSyntheticNovStatus = false,
): RecordChange[] {
  const changes: RecordChange[] = [];
  const oldFields = scalarFields(oldRecord);
  const newFields = scalarFields(newRecord);
  for (const key of new Set([...Object.keys(oldFields), ...Object.keys(newFields)])) {
    const before = oldFields[key] ?? 'Not present';
    const after = newFields[key] ?? 'Removed';
    if (before === after) continue;
    if (
      suppressSyntheticNovStatus &&
      newRecord.kind === 'nov' &&
      key === 'Status' &&
      before === 'Notice of Violation' &&
      /^(OPEN|CLOSED)$/i.test(after)
    )
      continue;
    changes.push({
      recordId: newRecord.id,
      kind: newRecord.kind,
      field: key,
      oldValue: before,
      newValue: after,
      sourceUrl: newRecord.sourceUrl,
      action: deadlineNote(after),
    });
  }

  const oldTables = tableRows(oldRecord);
  const newTables = tableRows(newRecord);
  for (const key of new Set([...oldTables.keys(), ...newTables.keys()])) {
    const before = oldTables.get(key)?.rows ?? new Map<string, number>();
    const after = newTables.get(key)?.rows ?? new Map<string, number>();
    for (const [row, count] of after) {
      for (let index = before.get(row) ?? 0; index < count; index += 1) {
        changes.push({
          recordId: newRecord.id,
          kind: newRecord.kind,
          field: `${key} · row added`,
          oldValue: 'Not present',
          newValue: row,
          sourceUrl: newRecord.sourceUrl,
          action: deadlineNote(row),
        });
      }
    }
    for (const [row, count] of before) {
      for (let index = after.get(row) ?? 0; index < count; index += 1) {
        changes.push({
          recordId: newRecord.id,
          kind: newRecord.kind,
          field: `${key} · row removed`,
          oldValue: row,
          newValue: 'Removed',
          sourceUrl: newRecord.sourceUrl,
          action: 'Confirm the removal in the official DBI record before relying on it.',
        });
      }
    }
  }

  changes.push(...diffAddenda(oldRecord, newRecord));
  changes.push(...diffEnforcement(oldRecord, newRecord));

  const oldLinks = recordLinks(oldRecord);
  const newLinks = recordLinks(newRecord);
  for (const link of newLinks) {
    if (oldLinks.has(link)) continue;
    changes.push({
      recordId: newRecord.id,
      kind: newRecord.kind,
      field: 'Document or linked record added',
      oldValue: 'Not present',
      newValue: link,
      sourceUrl: newRecord.sourceUrl,
      action: 'Review the newly linked official record or document.',
    });
  }
  for (const link of oldLinks) {
    if (newLinks.has(link)) continue;
    changes.push({
      recordId: newRecord.id,
      kind: newRecord.kind,
      field: 'Document or linked record removed',
      oldValue: link,
      newValue: 'Removed',
      sourceUrl: newRecord.sourceUrl,
      action: 'Confirm the removed link in the official DBI record before relying on it.',
    });
  }
  return changes;
}

function recorderAction(value: string) {
  if (/lien|default|abatement|restriction|notice|lis pendens|foreclosure/i.test(value)) {
    return 'This recorded document may affect title, financing, or a response deadline. Review the official index promptly and consult the relevant agency or a qualified professional if needed.';
  }
  if (/deed|reconveyance|trust|assignment/i.test(value)) {
    return 'Review the official Recorder index and confirm how this document affects ownership or financing before relying on the parcel record.';
  }
  return 'Review the updated entry in the official Recorder index.';
}

function recorderSummary(document: RecorderDocument) {
  const title = document.titles.join(' · ') || 'Recorded document';
  return `${title}${document.documentDate ? ` · ${document.documentDate}` : ''}`;
}

function diffRecorder(previous: RecorderSnapshot, current: RecorderSnapshot) {
  const changes: RecordChange[] = [];
  const oldDocuments = new Map(
    previous.documents.map((document) => [document.documentNumber, document]),
  );
  const newDocuments = new Map(
    current.documents.map((document) => [document.documentNumber, document]),
  );

  for (const [documentNumber, document] of newDocuments) {
    const oldDocument = oldDocuments.get(documentNumber);
    if (!oldDocument) {
      const summary = recorderSummary(document);
      changes.push({
        recordId: documentNumber,
        kind: 'recorder',
        field: 'Recorded document',
        oldValue: 'Not present',
        newValue: summary,
        sourceUrl: document.sourceUrl,
        action: recorderAction(summary),
      });
      continue;
    }

    for (const field of new Set([
      ...Object.keys(oldDocument.fields),
      ...Object.keys(document.fields),
    ])) {
      const before = oldDocument.fields[field] ?? 'Not present';
      const after = document.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: documentNumber,
        kind: 'recorder',
        field,
        oldValue: before,
        newValue: after,
        sourceUrl: document.sourceUrl,
        action: recorderAction(`${field} ${after}`),
      });
    }
  }

  for (const [documentNumber, document] of oldDocuments) {
    if (newDocuments.has(documentNumber)) continue;
    changes.push({
      recordId: documentNumber,
      kind: 'recorder',
      field: 'Recorded document',
      oldValue: recorderSummary(document),
      newValue: 'Removed from the published Recorder index',
      sourceUrl: previous.sourceUrl,
      action:
        'Confirm the removal or correction in the official Recorder index before relying on it.',
    });
  }
  return changes;
}

function subdivisionAction(value: string) {
  if (
    /deadline|due|returned|revision|mylars|recorded|approval|pending|review|notice/i.test(value)
  ) {
    return 'This subdivision update may require documents, review, approval, or another time-sensitive response. Check the official Public Works project promptly.';
  }
  return 'Review the complete update in the official Public Works subdivision project.';
}

function subdivisionSummary(project: SubdivisionProject) {
  return [project.name, project.type, project.phase, project.status].filter(Boolean).join(' · ');
}

function diffSubdivision(previous: SubdivisionSnapshot, current: SubdivisionSnapshot) {
  const changes: RecordChange[] = [];
  const oldProjects = new Map(previous.projects.map((project) => [project.id, project]));
  const newProjects = new Map(current.projects.map((project) => [project.id, project]));

  for (const [projectId, project] of newProjects) {
    const oldProject = oldProjects.get(projectId);
    if (!oldProject) {
      const summary = subdivisionSummary(project);
      changes.push({
        recordId: projectId,
        kind: 'subdivision',
        field: 'Subdivision project',
        oldValue: 'Not present',
        newValue: summary,
        sourceUrl: project.sourceUrl,
        action: subdivisionAction(summary),
      });
      continue;
    }

    for (const field of new Set([
      ...Object.keys(oldProject.fields),
      ...Object.keys(project.fields),
    ])) {
      const before = oldProject.fields[field] ?? 'Not present';
      const after = project.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: projectId,
        kind: 'subdivision',
        field,
        oldValue: before,
        newValue: after,
        sourceUrl: project.sourceUrl,
        action: subdivisionAction(`${field} ${after}`),
      });
    }

    const oldActivities = new Map(
      oldProject.activities.map((activity) => [activity.key, activity]),
    );
    const newActivities = new Map(project.activities.map((activity) => [activity.key, activity]));
    for (const [key, activity] of newActivities) {
      const oldActivity = oldActivities.get(key);
      if (!oldActivity) {
        const summary = [activity.date, activity.activity, activity.comments]
          .filter(Boolean)
          .join(' · ');
        changes.push({
          recordId: projectId,
          kind: 'subdivision',
          field: 'Subdivision activity · entry added',
          oldValue: 'Not present',
          newValue: summary,
          sourceUrl: project.sourceUrl,
          action: subdivisionAction(summary),
        });
        continue;
      }
      for (const field of ['activity', 'date', 'comments'] as const) {
        if (oldActivity[field] === activity[field]) continue;
        changes.push({
          recordId: projectId,
          kind: 'subdivision',
          field: `Subdivision activity · ${field}`,
          oldValue: oldActivity[field] || 'Not present',
          newValue: activity[field] || 'Removed',
          sourceUrl: project.sourceUrl,
          action: subdivisionAction(activity[field]),
        });
      }
    }
    for (const [key, activity] of oldActivities) {
      if (newActivities.has(key)) continue;
      changes.push({
        recordId: projectId,
        kind: 'subdivision',
        field: 'Subdivision activity · entry removed',
        oldValue: [activity.date, activity.activity, activity.comments].filter(Boolean).join(' · '),
        newValue: 'Removed',
        sourceUrl: project.sourceUrl,
        action: 'Confirm the removal in the official Public Works project before relying on it.',
      });
    }
  }

  for (const [projectId, project] of oldProjects) {
    if (newProjects.has(projectId)) continue;
    changes.push({
      recordId: projectId,
      kind: 'subdivision',
      field: 'Subdivision project',
      oldValue: subdivisionSummary(project),
      newValue: 'Removed from the published subdivision search',
      sourceUrl: previous.sourceUrl,
      action:
        'Confirm whether the project was completed, reassigned, or corrected in the official Public Works tracker.',
    });
  }
  return changes;
}

function sf311Summary(incident: Sf311Case) {
  return [incident.category, incident.subtype, incident.status, incident.updatedAt]
    .filter(Boolean)
    .join(' · ');
}

function sf311Action(value: string) {
  if (/hazard|blocked|unsafe|sewage|electrical|fire|urgent|violation/i.test(value)) {
    return 'Review the official SF311 case promptly and confirm whether the reported condition requires immediate follow-up.';
  }
  return 'Review the complete update on the official SF311 incident page.';
}

export function diffSf311(previous: Sf311Snapshot, current: Sf311Snapshot) {
  const changes: RecordChange[] = [];
  const oldCases = new Map(previous.cases.map((incident) => [incident.id, incident]));
  const newCases = new Map(current.cases.map((incident) => [incident.id, incident]));

  for (const [id, incident] of newCases) {
    const oldIncident = oldCases.get(id);
    if (!oldIncident) {
      const summary = sf311Summary(incident);
      changes.push({
        recordId: id,
        kind: '311',
        field: 'SF311 report',
        oldValue: 'Not present',
        newValue: summary,
        sourceUrl: sf311CaseSourceUrl(id),
        action: sf311Action(`${summary} ${incident.statusNotes}`),
      });
      continue;
    }

    for (const field of new Set([
      ...Object.keys(oldIncident.fields),
      ...Object.keys(incident.fields),
    ])) {
      const before = oldIncident.fields[field] ?? 'Not present';
      const after = incident.fields[field] ?? 'Removed';
      if (before === after) continue;
      changes.push({
        recordId: id,
        kind: '311',
        field,
        oldValue: before,
        newValue: after,
        sourceUrl: sf311CaseSourceUrl(id),
        action: sf311Action(`${field} ${after}`),
      });
    }
  }

  for (const [id, incident] of oldCases) {
    if (newCases.has(id)) continue;
    changes.push({
      recordId: id,
      kind: '311',
      field: 'SF311 report',
      oldValue: sf311Summary(incident),
      newValue: 'Removed from the published SF311 dataset',
      sourceUrl: sf311CaseSourceUrl(id),
      action:
        'Confirm the removal or correction on the official SF311 incident page before relying on it.',
    });
  }

  return changes;
}

function diffPublicWorks(previous: PublicWorksSnapshot, current: PublicWorksSnapshot) {
  if (previous.records.length >= 5 && current.records.length < previous.records.length * 0.6)
    throw new Error('Public Works returned an incomplete permit list; baseline was preserved.');
  const before = new Map(previous.records.map((record) => [record.id, record]));
  const after = new Map(current.records.map((record) => [record.id, record]));
  const changes: RecordChange[] = [];
  for (const id of new Set([...before.keys(), ...after.keys()])) {
    const oldRecord = before.get(id);
    const record = after.get(id);
    const display = record ?? oldRecord!;
    const scope =
      display.locationScope === 'address'
        ? 'Listed at this address'
        : 'On this block — not confirmed at this property';
    const recordChanges =
      oldRecord && record
        ? diffRecord(oldRecord, record, false)
        : [
            {
              recordId: id,
              kind: 'public-works' as const,
              field: 'Public Works permit',
              oldValue: oldRecord ? oldRecord.description : 'Not present',
              newValue: record ? record.description : 'Removed from the published source',
              sourceUrl: display.sourceUrl,
              action: '',
            },
          ];
    changes.push(
      ...recordChanges.map((change) => ({
        ...change,
        field: `${scope} · ${change.field}`,
        action: `${scope}: ${display.address}. Permit dates are an authorized work window, not a confirmed construction schedule. Review the official permit.`,
      })),
    );
  }
  return changes;
}

export function compareSnapshots(
  previous: ParcelSnapshot,
  current: ParcelSnapshot,
): RecordChange[] {
  if (
    !current.health.complete ||
    current.assessor === undefined ||
    current.recorder === undefined ||
    current.subdivision === undefined ||
    current.sf311 === undefined ||
    (previous.publicWorks !== undefined && current.publicWorks === undefined) ||
    current.safetyCompliance === undefined
  ) {
    throw new Error('The current parcel response appears incomplete; baseline was preserved.');
  }
  const upgradingComplianceDedupe =
    (previous.parserVersion ?? 1) < 10 && (current.parserVersion ?? 1) >= 10;
  const previousSafety = previous.safetyCompliance;
  const comparisonSafety =
    previousSafety && upgradingComplianceDedupe
      ? {
          ...previousSafety,
          statements: {
            ...previousSafety.statements,
            statements: removeProvenComplianceDuplicates(
              previousSafety.statements.statements,
              current.safetyCompliance.statements.statements,
            ),
          },
        }
      : previousSafety;
  if (previous.records.length >= 5 && current.records.length < previous.records.length * 0.6) {
    throw new Error('The DBI response appears incomplete; baseline was preserved.');
  }
  if (
    previous.recorder &&
    previous.recorder.documents.length >= 5 &&
    current.recorder.documents.length < previous.recorder.documents.length * 0.6
  ) {
    throw new Error('The Recorder response appears incomplete; baseline was preserved.');
  }
  if (
    previous.subdivision &&
    previous.subdivision.projects.length >= 3 &&
    current.subdivision.projects.length < previous.subdivision.projects.length * 0.6
  ) {
    throw new Error(
      'The Public Works subdivision response appears incomplete; baseline was preserved.',
    );
  }
  if (
    previous.sf311 &&
    previous.sf311.cases.length >= 5 &&
    current.sf311.cases.length < previous.sf311.cases.length * 0.6
  ) {
    throw new Error('The SF311 response appears incomplete; baseline was preserved.');
  }
  if (
    previous.safetyCompliance &&
    comparisonSafety &&
    ((previous.safetyCompliance.section604.affidavits.length >= 5 &&
      current.safetyCompliance.section604.affidavits.length <
        previous.safetyCompliance.section604.affidavits.length * 0.6) ||
      (previous.safetyCompliance.fireInspections.inspections.length >= 5 &&
        current.safetyCompliance.fireInspections.inspections.length <
          previous.safetyCompliance.fireInspections.inspections.length * 0.6) ||
      (previous.safetyCompliance.fireViolations.violations.length >= 5 &&
        current.safetyCompliance.fireViolations.violations.length <
          previous.safetyCompliance.fireViolations.violations.length * 0.6) ||
      // Keep the original trigger size: deduplication must not disable loss
      // protection for unique documents in an otherwise duplicate-heavy set.
      (previous.safetyCompliance.statements.statements.length >= 5 &&
        current.safetyCompliance.statements.statements.length <
          comparisonSafety.statements.statements.length * 0.6))
  ) {
    throw new Error('A fire safety source appears incomplete; baseline was preserved.');
  }

  const oldRecords = new Map(
    previous.records.map((record) => [`${record.kind}:${record.id}`, record]),
  );
  const newRecords = new Map(
    current.records.map((record) => [`${record.kind}:${record.id}`, record]),
  );
  const changes: RecordChange[] = [];
  const upgradingNovParser = (previous.parserVersion ?? 1) < 2 && (current.parserVersion ?? 1) >= 2;
  const upgradingAssessmentHistory =
    (previous.parserVersion ?? 1) < 3 && (current.parserVersion ?? 1) >= 3;
  const upgradingEnforcementStatus =
    (previous.parserVersion ?? 1) < 5 && (current.parserVersion ?? 1) >= 5;
  const upgradingSafetyCompliance =
    previous.safetyCompliance === undefined ||
    ((previous.parserVersion ?? 1) < 8 && (current.parserVersion ?? 1) >= 8);
  const expandingCondoSafetyScope =
    (previous.parserVersion ?? 1) < 9 &&
    (current.parserVersion ?? 1) >= 9 &&
    current.safetyCompliance.scope?.type === 'condo-building';

  for (const [key, record] of newRecords) {
    const oldRecord = oldRecords.get(key);
    if (!oldRecord) {
      changes.push({
        recordId: record.id,
        kind: record.kind,
        field: 'Record',
        oldValue: 'Not present',
        newValue: `New ${propertyRecordKindLabel(record.kind)} (${record.status})`,
        sourceUrl: record.sourceUrl,
        action: deadlineNote(`${record.status} ${record.description}`),
      });
      continue;
    }
    if (upgradingNovParser && record.kind === 'nov') continue;
    changes.push(...diffRecord(oldRecord, record, upgradingEnforcementStatus));
  }

  for (const [key, record] of oldRecords) {
    if (newRecords.has(key)) continue;
    changes.push({
      recordId: record.id,
      kind: record.kind,
      field: 'Record',
      oldValue: `${propertyRecordKindLabel(record.kind)} (${record.status})`,
      newValue: 'Removed from the current parcel record',
      sourceUrl: record.sourceUrl,
      action:
        'Confirm the removal in DBI; parcel reassignment or record corrections can require separate research.',
    });
  }
  if (previous.assessor !== undefined) {
    changes.push(
      ...diffAssessor(
        previous.assessor,
        current.assessor,
        current.block,
        current.lot,
        !upgradingAssessmentHistory,
      ),
    );
  }
  if (previous.recorder !== undefined) {
    changes.push(...diffRecorder(previous.recorder, current.recorder));
  }
  if (previous.subdivision !== undefined) {
    changes.push(...diffSubdivision(previous.subdivision, current.subdivision));
  }
  // Adopt the new source silently on existing baselines; history is not new work.
  if (previous.publicWorks && current.publicWorks) {
    changes.push(...diffPublicWorks(previous.publicWorks, current.publicWorks));
  }
  if (previous.sf311 !== undefined) {
    changes.push(...diffSf311(previous.sf311, current.sf311));
  }
  if (upgradingSafetyCompliance) {
    changes.push(...newSafetySourceChanges(current.safetyCompliance));
  } else if (comparisonSafety !== undefined) {
    const safetyChanges = diffSafetyCompliance(comparisonSafety, current.safetyCompliance);
    if (expandingCondoSafetyScope) {
      changes.push(...safetyChanges.filter((change) => change.oldValue !== 'Not present'));
      changes.push(...expandedCondoSafetyChanges(comparisonSafety, current.safetyCompliance));
    } else {
      changes.push(...safetyChanges);
    }
  }
  // Associate links at presentation time instead of copying complaint history
  // into every NOV baseline, which would notify repeatedly for the same event.
  const relatedRecords = [
    ...newRecords.values(),
    ...[...oldRecords].filter(([key]) => !newRecords.has(key)).map(([, record]) => record),
  ];
  return changes.map((change) => {
    // Translate only after matching raw fields, tables, and records. Distinct
    // city keys can share a display label without losing either change.
    const displayChange = {
      ...change,
      presentationVersion: 1 as const,
      field: propertyFieldLabel(change.field, change.kind),
    };
    if (change.kind !== 'complaint' && change.kind !== 'nov') return displayChange;
    const key = `${change.kind}:${change.recordId}`;
    const record = newRecords.get(key) ?? oldRecords.get(key);
    if (!record) return displayChange;
    const relatedSources = relatedEnforcementSources(record, relatedRecords);
    return relatedSources.length ? { ...displayChange, relatedSources } : displayChange;
  });
}

/** Stable content identity; source retrieval timestamps do not create daily change alerts. */
export async function fingerprintSnapshot(snapshot: ParcelSnapshot) {
  function normalize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(normalize);
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([key]) => !['fetchedAt', 'checkedAt', 'health'].includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, child]) => [key, normalize(child)]),
      );
    }
    return value;
  }
  const data = new TextEncoder().encode(JSON.stringify(normalize(snapshot)));
  const hash = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
